import React, { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, Field, Form } from 'formik';
// import { Translate } from "react-localize-redux";
import { connect, useDispatch } from 'react-redux';
import moment from 'moment';
import InfoIcon from '@material-ui/icons/Info';
import Tooltip from '@material-ui/core/Tooltip';

import Logo from 'components/FormUI/Logo';
import Input from 'components/FormUI/Input';
import Select from 'components/FormUI/Select';
import MaskedInput from 'components/FormUI/MaskedInput';
import Checkbox from '@material-ui/core/Checkbox';
import Button from 'components/FormUI/Button';
import CommonErrorMessage from 'components/FormUI/CommonErrorMessage';
import { ModalTermsAndConditions } from 'components/Modals/TermsAndConditions';
import { Card, CardBody } from 'components/UI/Card';
import { getSignUpValidationSchema } from 'utils/validation';
import * as userActions from 'actions/user';
import { colors } from 'utils/styles';
import { DATE_FORMATS } from 'utils/datesAndMoney';
import { redirectTo } from 'services/links';
import { setTemporaryDataForSwtichingRole } from 'actions/update-user';
import useRouter from 'hooks/useRouter';
import { UserRoles } from 'helpers/constants';
import { TOOLTIP, ROUTES } from '../../constants';

const useStyles = makeStyles(theme => ({
  popover: {
    pointerEvents: 'none',
    fontSize: 14,
  },
  paper: {
    padding: theme.spacing(1),
    transform: 'translateY(-10px) !important',
    maxWidth: 500,
  },
  container: {
    maxWidth: 530,
  },
}));

const WelcomeMessage = styled.h1`
  font-size: 1.675rem;
  text-align: center;
  font-weight: 500;
  line-height: normal;
  margin-bottom: 1.5rem;
`;

const AgreementContainer = styled.div`
  text-align: center;
  margin: 2rem 0;
  line-height: 1.28;
  letter-spacing: 0.25px;
  font-weight: 500;
  font-size: 0.9375rem;
  width: 100%;

  a {
    color: inherit;
  }
`;

const ButtonsContainer = styled.div`
  margin-top: 2.5rem;
  text-align: center;
  width: 100%;

  & > * + * {
    margin-top: 1.5rem;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  line-height: 1.17;
  letter-spacing: 0.4px;
  font-size: 0.875rem;
  font-weight: 300;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledMainCard = styled(Card)`
  max-width: 580px;
  margin: auto;
`;

const StyledTerms = styled.a`
  color: ${colors.darkOceanBlue} !important;
`;

function SignUpSecondScreen({ loading, user, continueRegister, error }) {
  const dispatch = useDispatch();
  const router = useRouter();
  const classes = useStyles();
  const { pathname } = useLocation();
  const [showTerms, setShowTerms] = useState(false);
  const isAddingCoachRole = pathname === ROUTES.ADD_ROLE;

  const registerHandler = useCallback(
    values => {
      if (isAddingCoachRole) {
        const data = {
          businessName: values.BusinessName,
          certification: values.Certification,
          occupation: values.Occupation,
          businessType: values.BusinessType,
        };
        if (values.CustomerLabelPreference) {
          data.customerLabelPreference = values.CustomerLabelPreference;
        }

        try {
          dispatch(setTemporaryDataForSwtichingRole(data));
          router.push(ROUTES.SUBMIT_ADD_ROLE);
        } catch {
          redirectTo(ROUTES.DASHBOARD);
        }
      } else {
        const data = {
          ...values,
          accountId: user.accountId,
          BirthDate: moment.utc(values.BirthDate).format(),
          IsCohealer: true,
        };

        if (!data.CustomerLabelPreference) {
          delete data.CustomerLabelPreference;
        }

        continueRegister({ fields: data, userView: UserRoles.cohealer });
      }
    },
    [continueRegister, user.accountId, isAddingCoachRole],
  );

  return (
    <>
      <StyledMainCard>
        <CardBody>
          <Grid
            container
            direction="column"
            alignItems="stretch"
            classes={{
              container: classes.container,
            }}
          >
            <Grid container item>
              <Logo />
            </Grid>
            <Grid item>
              <WelcomeMessage>
                Register as a coach{' '}
                <Tooltip
                  title="Are you an inspiring coach, teacher, or mentor in the field of personal,
                  professional, or spiritual development? Then this platform is for you!
                  Cohere helps you scale your businesses using a single platform. Your
                  customers can purchase and participate in your live courses, work with
                  you One-on-One, and join your community of existing clients and
                  customers."
                  arrow
                  enterTouchDelay={TOOLTIP.ENTER_DELAY}
                  leaveTouchDelay={TOOLTIP.LEAVE_DELAY}
                >
                  <InfoIcon htmlColor={colors.lightBrown} />
                </Tooltip>
              </WelcomeMessage>
            </Grid>

            <Formik
              initialValues={{
                FirstName: user.firstName || '',
                LastName: user.lastName || '',
                BirthDate: isAddingCoachRole ? moment(user.birthDate).format(DATE_FORMATS.DAY) : '',
                BusinessName: '',
                BusinessType: '',
                CustomBusinessType: '',
                Certification: '',
                Occupation: '',
                CustomerLabelPreference: '',
                TimeZoneId: user.timeZoneId || '',
                HasAgreedToTerms: false,
              }}
              // validationSchema={getSignUpValidationSchema(Translate)}
              validationSchema={getSignUpValidationSchema()}
              onSubmit={registerHandler}
            >
              {({ values }) => (
                <Form>
                  <Grid container spacing={4}>
                    <Grid item sm={4} xs={12}>
                      <Input label="First Name" type="text" name="FirstName" disabled={isAddingCoachRole} fullWidth />
                    </Grid>
                    <Grid item sm={4} xs={12}>
                      <Input label="Last Name" type="text" name="LastName" disabled={isAddingCoachRole} fullWidth />
                    </Grid>
                    <Grid item sm={4} xs={12}>
                      <Input
                        label="Date of Birth"
                        mask="date"
                        type="text"
                        name="BirthDate"
                        disabled={isAddingCoachRole}
                        inputComponent={MaskedInput}
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={4}>
                    <Grid item sm={4} xs={12}>
                      <Input label="Business Name" type="text" name="BusinessName" helperText="optional" fullWidth />
                    </Grid>
                    <Grid item sm={4} xs={12}>
                      <Select
                        label="Business Type"
                        type="text"
                        name="BusinessType"
                        fullWidth
                        items={[
                          { title: 'Coaching', value: 'Coaching' },
                          { title: 'Teaching', value: 'Teaching' },
                          { title: 'Mentoring', value: 'Mentoring' },
                          { title: 'Consulting', value: 'Consulting' },
                          { title: 'Other', value: 'Other' },
                        ]}
                      />
                    </Grid>
                    <Grid item sm={4} xs={12}>
                      <Input
                        label="Certification/Degree"
                        type="text"
                        name="Certification"
                        fullWidth
                        helperText="optional"
                      />
                    </Grid>
                  </Grid>

                  {values.BusinessType === 'Other' && (
                    <Grid container spacing={4}>
                      <Grid item sm={12} xs={12}>
                        <Input label="Business Type" type="text" name="CustomBusinessType" fullWidth />
                      </Grid>
                    </Grid>
                  )}

                  <Grid container spacing={4}>
                    <Grid item sm={6} xs={12}>
                      <Input label="Occupation" type="text" name="Occupation" helperText="optional" fullWidth />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Select
                        label="What do you call your customers?"
                        type="text"
                        name="CustomerLabelPreference"
                        fullWidth
                        helperText="optional"
                        items={[
                          { title: <em>None</em>, value: '' },
                          { title: 'Clients', value: 'Clients' },
                          { title: 'Students', value: 'Students' },
                          { title: 'Customers', value: 'Customers' },
                        ]}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={4}>
                    <Grid item xs={12}>
                      <Select
                        label="Timezone"
                        name="TimeZoneId"
                        fullWidth
                        disabled={isAddingCoachRole}
                        items={[
                          {
                            value: 'America/Los_Angeles',
                            title: 'Pacific Standard Time',
                          },
                          {
                            value: 'America/Denver',
                            title: 'Mountain Standard Time',
                          },
                          {
                            value: 'America/Chicago',
                            title: 'Central Standard Time',
                          },
                          {
                            value: 'America/New_York',
                            title: 'Eastern Standard Time',
                          },
                          {
                            value: 'America/Anchorage',
                            title: 'Alaskan Standard Time',
                          },
                          {
                            value: 'America/St_Johns',
                            title: 'Newfoundland Standard Time',
                          },
                          {
                            value: 'Pacific/Honolulu',
                            title: 'Hawaiian Standard Time',
                          },
                          {
                            value: 'America/Phoenix',
                            title: 'Arizona Standard Time',
                          },
                          {
                            value: 'Australia/Sydney',
                            title: 'Australia/Sydney',
                          },
                          {
                            value: 'Canada/Atlantic',
                            title: 'Canada/Atlantic',
                          },
                          { value: 'Canada/Central', title: 'Canada/Central' },
                          { value: 'Canada/Eastern', title: 'Canada/Eastern' },
                          {
                            value: 'Canada/Mountain',
                            title: 'Canada/Mountain',
                          },
                          {
                            value: 'Canada/Newfoundland',
                            title: 'Canada/Newfoundland',
                          },
                          { value: 'Canada/Pacific', title: 'Canada/Pacific' },
                          {
                            value: 'Canada/Saskatchewan',
                            title: 'Canada/Saskatchewan',
                          },
                          { value: 'Canada/Yukon', title: 'Canada/Yukon' },
                          {
                            value: 'Europe/Amsterdam',
                            title: 'Europe/Amsterdam',
                          },
                          { value: 'Europe/Andorra', title: 'Europe/Andorra' },
                          { value: 'Europe/Athens', title: 'Europe/Athens' },
                          { value: 'Europe/Belfast', title: 'Europe/Belfast' },
                          {
                            value: 'Europe/Belgrade',
                            title: 'Europe/Belgrade',
                          },
                          { value: 'Europe/Berlin', title: 'Europe/Berlin' },
                          {
                            value: 'Europe/Bratislava',
                            title: 'Europe/Bratislava',
                          },
                          {
                            value: 'Europe/Brussels',
                            title: 'Europe/Brussels',
                          },
                          {
                            value: 'Europe/Bucharest',
                            title: 'Europe/Bucharest',
                          },
                          {
                            value: 'Europe/Budapest',
                            title: 'Europe/Budapest',
                          },
                          {
                            value: 'Europe/Busingen',
                            title: 'Europe/Busingen',
                          },
                          {
                            value: 'Europe/Chisinau',
                            title: 'Europe/Chisinau',
                          },
                          {
                            value: 'Europe/Copenhagen',
                            title: 'Europe/Copenhagen',
                          },
                          { value: 'Europe/Dublin', title: 'Europe/Dublin' },
                          {
                            value: 'Europe/Gibraltar',
                            title: 'Europe/Gibraltar',
                          },
                          {
                            value: 'Europe/Guernsey',
                            title: 'Europe/Guernsey',
                          },
                          {
                            value: 'Europe/Helsinki',
                            title: 'Europe/Helsinki',
                          },
                          {
                            value: 'Europe/Isle_of_Man',
                            title: 'Europe/Isle of Man',
                          },
                          {
                            value: 'Europe/Istanbul',
                            title: 'Europe/Istanbul',
                          },
                          { value: 'Europe/Jersey', title: 'Europe/Jersey' },
                          { value: 'Europe/Kaliningrad', title: 'Europe/Kiev' },
                          { value: 'Europe/Lisbon', title: 'Europe/Lisbon' },
                          {
                            value: 'Europe/Ljubljana',
                            title: 'Europe/Ljubljana',
                          },
                          { value: 'Europe/London', title: 'Europe/London' },
                          {
                            value: 'Europe/Luxembourg',
                            title: 'Europe/Luxembourg',
                          },
                          { value: 'Europe/Madrid', title: 'Europe/Madrid' },
                          { value: 'Europe/Malta', title: 'Europe/Malta' },
                          {
                            value: 'Europe/Mariehamn',
                            title: 'Europe/Mariehamn',
                          },
                          { value: 'Europe/Minsk', title: 'Europe/Minsk' },
                          { value: 'Europe/Monaco', title: 'Europe/Monaco' },
                          { value: 'Europe/Moscow', title: 'Europe/Moscow' },
                          { value: 'Europe/Nicosia', title: 'Europe/Nicosia' },
                          { value: 'Europe/Oslo', title: 'Europe/Oslo' },
                          { value: 'Europe/Paris', title: 'Europe/Paris' },
                          {
                            value: 'Europe/Podgorica',
                            title: 'Europe/Podgorica',
                          },
                          { value: 'Europe/Prague', title: 'Europe/Prague' },
                          { value: 'Europe/Riga', title: 'Europe/Riga' },
                          { value: 'Europe/Rome', title: 'Europe/Rome' },
                          { value: 'Europe/Samara', title: 'Europe/Samara' },
                          {
                            value: 'Europe/San_Marino',
                            title: 'Europe/San_Marino',
                          },
                          {
                            value: 'Europe/Sarajevo',
                            title: 'Europe/Sarajevo',
                          },
                          {
                            value: 'Europe/Simferopol',
                            title: 'Europe/Simferopol',
                          },
                          { value: 'Europe/Skopje', title: 'Europe/Skopje' },
                          { value: 'Europe/Sofia', title: 'Europe/Sofia' },
                          {
                            value: 'Europe/Stockholm',
                            title: 'Europe/Stockholm',
                          },
                          { value: 'Europe/Tallinn', title: 'Europe/Tallinn' },
                          { value: 'Europe/Tirane', title: 'Europe/Tirane' },
                          {
                            value: 'Europe/Tiraspol',
                            title: 'Europe/Tiraspol',
                          },
                          {
                            value: 'Europe/Uzhgorod',
                            title: 'Europe/Uzhgorod',
                          },
                          { value: 'Europe/Vaduz', title: 'Europe/Vaduz' },
                          { value: 'Europe/Vatican', title: 'Europe/Vatican' },
                          { value: 'Europe/Vienna', title: 'Europe/Vienna' },
                          { value: 'Europe/Vilnius', title: 'Europe/Vilnius' },
                          {
                            value: 'Europe/Volgograd',
                            title: 'Europe/Volgograd',
                          },
                          { value: 'Europe/Warsaw', title: 'Europe/Warsaw' },
                          { value: 'Europe/Zagreb', title: 'Europe/Zagreb' },
                          {
                            value: 'Europe/Zaporozhye',
                            title: 'Europe/Zaporozhye',
                          },
                          { value: 'Europe/Zurich', title: 'Europe/Zurich' },
                          { value: 'Hongkong', title: 'Hongkong' },
                          { value: 'Iceland', title: 'Iceland' },
                          { value: 'Israel', title: 'Israel' },
                          { value: 'Japan', title: 'Japan' },
                          {
                            value: 'Mexico/BajaNorte',
                            title: 'Mexico/Pacific',
                          },
                          { value: 'Mexico/BajaSur', title: 'Mexico/Mountain' },
                          { value: 'Mexico/General', title: 'Mexico/Central' },
                          { value: 'Navajo', title: 'Navajo' },
                          { value: 'NZ', title: 'NZ' },
                          { value: 'NZ-CHAT', title: 'NZ-CHAT' },
                          { value: 'Singapore', title: 'Singapore' },
                        ]}
                      />
                    </Grid>
                  </Grid>

                  <Grid container>
                    <AgreementContainer>
                      <Field as={Checkbox} name="HasAgreedToTerms" type="checkbox" color="primary" />
                      &nbsp;&nbsp;&nbsp;I agree to the{' '}
                      <StyledTerms
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          setShowTerms(true);
                        }}
                      >
                        Terms and Conditions
                      </StyledTerms>
                    </AgreementContainer>
                  </Grid>

                  {error?.message && <CommonErrorMessage message={error.message} />}

                  <Grid container>
                    <ButtonsContainer>
                      <Button disabled={!values.HasAgreedToTerms || loading} type="submit">
                        Join Cohere
                      </Button>
                      <div>
                        <StyledLink to="/auth/signup/client/continue">Not a service provider? Click here.</StyledLink>
                      </div>
                    </ButtonsContainer>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </CardBody>
      </StyledMainCard>

      <ModalTermsAndConditions showTerms={showTerms} onCancel={() => setShowTerms(false)} />
    </>
  );
}

SignUpSecondScreen.propTypes = {
  loading: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    accountId: PropTypes.string,
  }).isRequired,
  continueRegister: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      message: PropTypes.string,
    }),
  ]),
};

SignUpSecondScreen.defaultProps = {
  error: null,
};

const mapStateToProps = ({ account }) => ({
  user: account?.user || {},
  loading: account?.isLoading,
  error: account?.error,
});

const actions = {
  continueRegister: userActions.continueRegister,
};

export default connect(mapStateToProps, actions)(SignUpSecondScreen);
